import { useMemo } from "react";
import { useAtomValue } from "jotai";
import useContentPermissions from "@src/features/libraryItem/hooks/useContentPermissions";
import { ContentPermissionsFragment } from "@src/features/libraryItem/hooks/useContentPermissions.generated";
import useSkillBuilderStore from "@src/features/libraryItem/skill/hooks/useSkillBuilderStore";
import { publishedVersionSelectedAtom } from "@src/features/libraryItem/module/atoms";
import {
  useActiveLibraryItemMainTab,
  useIsRedesignedLibraryItemPage,
} from "@src/features/libraryItem/contexts/libraryItemMainTabStore";

const useSkillBuilderContentPermissions = ():
  | ContentPermissionsFragment
  | undefined => {
  const skillId = useSkillBuilderStore((state) => state.skillId);
  const isDraftVersion = useSkillBuilderStore(
    (state) => state.draftVersionSelected,
  );
  const permissions = useContentPermissions({
    skillId,
  });
  // On the redesigned module page a check-in opened inside the module's
  // published version is read-only even when the check-in's own selected
  // version is its draft — the module's version choice governs everything on
  // its canvas. The "preview" tab is the read-only draft view and masks the
  // same way. The store reports nothing on the previous page.
  const redesigned = useIsRedesignedLibraryItemPage();
  const publishedSelected = !!useAtomValue(publishedVersionSelectedAtom);
  const modulePublishedSelected = redesigned && publishedSelected;
  const viewingTab = useActiveLibraryItemMainTab() === "preview";

  return useMemo(() => {
    if (!permissions) return undefined;
    if (isDraftVersion && !modulePublishedSelected && !viewingTab)
      return permissions;
    return {
      ...permissions,
      hasEditContentPermission: false,
    };
  }, [permissions, isDraftVersion, modulePublishedSelected, viewingTab]);
};

export default useSkillBuilderContentPermissions;
